import { useCallback, useState } from 'react';
import { Prompt, type PromptProps } from 'react-router-dom';
import type History from 'history';
import { push, replace } from 'redux-first-history';
import { useDispatch } from '@s20/hooks/redux.hooks';
import { Modal } from '../../../../components/common/Modal';

const defaultMessage = 'You have unsaved changes. Are you sure you want to leave?';

interface ExtendedHistory extends History.Location {
    action: History.Action;
    location: History.Location;
};

export const FormPrompt = ({ when, message = defaultMessage, ...BasePromptProps }: PromptProps) => {
    const dispatch = useDispatch();
    const [requiresConfirmation, setRequiresConfirmation] = useState(true);

    const handleAlert = useCallback((history: ExtendedHistory, historyAction: History.Action) => {
        if (!requiresConfirmation) {
            return false;
        }

        Modal.show({
            onSubmit: () => {
                const action = historyAction || history.action;
                const { location } = history;

                const pathname = location?.pathname || history?.pathname || '';
                const search = location?.search || history?.search || '';

                const to = `${pathname}${search}`;

                setRequiresConfirmation(false);

                switch (action) {
                    case 'PUSH': {
                        // Hacky, but it needs to be a different "event" from the initial navigation event
                        setTimeout(() => {
                            dispatch(push(to));
                        }, 1);

                        break;
                    }
                    case 'REPLACE': {
                        setTimeout(() => {
                            dispatch(replace(to));
                        }, 1);

                        break;
                    }
                    default: {
                        break;
                    }
                }
            },
            title: 'Unsaved Changes',
            body: message,
            SaveButtonProps: {
                label: 'Continue without saving',
                icon: null,
                color: 'outline-primary',
            },
        });

        return true;
    }, [message, dispatch, requiresConfirmation]);

    return (
        <Prompt
            {...BasePromptProps}
            when={when ? requiresConfirmation : when}
            message={handleAlert as (location: History.Location) => boolean}
        />
    );
};